import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import _ from 'lodash';
import { Party } from './Party';
import { Address } from './Address';
import { FamilyHistory } from './FamilyHistory';
import { PersonalHistory } from './PersonalHistory';
import { RiskFactors } from './RiskFactors';
import { ExistingPolicies } from './ExistingPolicies';
import { Financials } from './Financials';
import { Customer } from './Customer';
import { PolicyQuery } from './PolicyQuery';
import { Person } from './Person';
import { Signer } from './Signer';
import { Phone } from './Phone';
import { BrothersAndSisters } from './BrothersAndSisters';
import { Applicant as GQLApplicant } from '@hit/hit-api-client';
import { ChronicIllnessRiderSupplement } from './ChronicIllnessRiderSupplement';

@supertypeClass({ toClient: true, toServer: true })
export class Applicant extends Everything implements GQLApplicant {
  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property()
  overrideFace;

  @property({ comment: 'Personal info of the insured', getType: () => Party })
  party: Party;

  @property({
    comment: 'Related party to the insured that signs on their behalf',
    getType: () => Person,
  })
  signer: Person;

  @property({
    comment: 'Denotes whether the applicant/insured will have a signer (that is not the Owner) on their behalf',
  })
  hasSigner: boolean;

  @property({ rule: 'required', values: { male: 'Male', female: 'Female' } })
  gender: string;

  @property({
    values: ['P+', 'Pf', 'R+', 'Rg', 'P+S', 'PfS', 'R+S', 'RgS'],
    descriptions: {
      'P+': 'Preferred Plus',
      'Pf': 'Preferred',
      'R+': 'Regular Plus',
      'Rg': 'Regular',
      'P+S': 'Preferred Plus Smoker',
      'PfS': 'Preferred Smoker',
      'R+S': 'Regular Plus Smoker',
      'RgS': 'Regular Smoker',
    },
    comment: 'Health class of the indured',
  })
  healthClass: string = 'P+';

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 500)', comment: 'Weight in lbs' })
  weight: number;

  @property({ comment: 'Height in inches: heightFeet*12+heightInches' })
  height: number;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(1, 9)', comment: 'Height feet: foot part of Height' })
  heightFeet: number;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 11)',
    comment: 'Height inches: inch part of Height',
  })
  heightInches: number;

  /**
   * ALl of the following fields are reserved until we decide what to do with Vantage policies
   * In the mean time they SHOULD NOT BE USED   !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   * All of the equivalent fields are in familyHistory, personalHistory
   */
  @property({ values: { me: 'Me', doctor: 'My doctor' } })
  hivSendTo: string = 'me';

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property()
  doctorName: string;

  @property({ getType: () => Address })
  doctorAddress: Address;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ values: { false: 'No', true: 'Yes' } })
  turnedDown: boolean;

  @property({ values: [0, 1, 2, 3], descriptions: { '0': 'Fair', '1': 'Good', '2': 'Very Good', '3': 'Excellent' } })
  healthClassNumber: number = 3;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true })
  healthClassNumberLongDesc = {
    '0': 'Your blood pressure or cholesterol levels are high, you should lose some weight or you have a history of medical conditions.  You aren’t in bad health, but you are squarely average.',
    '1': 'You are in pretty good health although you may have somewhat high blood pressure or cholesterol and could stand to lose some weight. You may have some history of medical conditions.',
    '2': 'You generally eat right, aren’t overweight and have good cholesterol and blood pressure levels, either with or without medication. You’re definitely in the top half of the population in terms of overall health.',
    '3': "Your cholesterol and blood pressure are normal, you aren't overweight, and don't have any medical conditions. A picture of perfect health.  Congrats, you are part of the healthiest 20%.",
  };

  @property({ isLocal: true })
  healthRank = { 'P+': 4, 'Pf': 3, 'R+': 2, 'Rg': 1, 'P+S': 4, 'PfS': 3, 'R+S': 2, 'RgS': 1 };

  @property()
  bloodPressureKnown: boolean;

  @property()
  bloodPressureHigh: boolean;

  @property()
  bloodPressureRange: string = '0';

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true })
  bloodPressureRangeChoices = [0, 1, 2, 3];

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true })
  bloodPressureRangeChoiceValues = { '0': 'Normal', '1': 'Slightly High', '2': 'High', '3': 'Very High' };

  @property({ isLocal: true })
  bloodPressureSystolicRanges = { '0': 120, '1': 140, '2': 160, '3': 180 };

  @property({ isLocal: true })
  bloodPressureDystolicRanges = { '0': 80, '1': 90, '2': 100, '3': 110 };

  @property({ rule: ['numeric'] })
  bloodPressureSystolic: number = 120;

  @property({ rule: ['numeric'] })
  bloodPressureDystolic: number = 80;

  @property()
  bloodPressureMedicationUsed: boolean;

  @property({ rule: ['numeric'] })
  bloodPressureTreatedPeriod: number;

  @property({ rule: ['numeric'] })
  bloodPressureControlledPeriod: number;

  @property()
  cholesterolKnown: boolean;

  @property()
  cholesterolHigh: boolean;

  @property({ rule: ['numeric'] })
  cholesterolLevel: number = 200;

  @property({ rule: ['numeric'] })
  cholesterolRange: number;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true, getType: () => Number })
  cholesterolRangeChoices: Array<number> = [0, 1, 2, 3];

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true })
  cholesterolRangeChoiceValues = { '0': 'Normal', '1': 'Slightly High', '2': 'High', '3': 'Very High' };

  @property({ isLocal: true })
  cholesterolLevelRanges = { '0': 200, '1': 220, '2': 240, '3': 300 };

  @property({ isLocal: true })
  cholesterolRatioRanges = { '0': 3.5, '1': 5.5, '2': 7, '3': 7.5 };

  @property({ rule: ['numeric'] })
  cholesterolRatio: number = 5;

  @property()
  cholesterolMedicationUsed: boolean;

  @property({ rule: ['numeric'] })
  cholesterolTreatedPeriod: number;

  @property({ rule: ['numeric'] })
  cholesterolControlledPeriod: number;

  /* As long as we are not getting into details of tobacco this is the sole flag for tobacco use*/
  @property({ values: { false: 'I do not use tobacco/nicotine', true: 'I do use tobacco/nicotine' } })
  smoker: boolean = false;

  @property()
  cigarettesUsed: boolean = true;

  @property({ rule: ['numeric'], validate: 'isWithin(1, 99)' })
  cigarettesQuantity: number = 20;

  @property({ rule: ['numeric'] })
  cigarettesPeriodLastUsed: number;

  @property()
  cigarsUsed: boolean;

  @property({ rule: ['numeric'] })
  cigarsQuantity: number = 1;

  @property({ rule: ['numeric'] })
  cigarsPeriodLastUsed: number;

  @property()
  pipeUsed: boolean;

  @property({ rule: ['numeric'] })
  pipeQuantity: number = 20;

  @property({ rule: ['numeric'] })
  pipePeriodLastUsed: number;

  @property()
  chewingTobaccoUsed: boolean;

  @property({ rule: ['numeric'] })
  chewingTobaccoQuantity: number = 20;

  @property({ rule: ['numeric'] })
  chewingTobaccoPeriodLastUsed: number;

  @property()
  nicotineGumUsed: boolean;

  @property({ rule: ['numeric'] })
  nicotineGumQuantity: number = 20;

  @property({ rule: ['numeric'] })
  nicotineGumPeriodLastUsed: number;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true, getType: () => String })
  periodChoices: Array<string> = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ isLocal: true })
  periodChoiceDescriptions = {
    '0': 'less than 6 months',
    '1': '6 months - 1 year',
    '2': '1-2 years',
    '3': '2-3 years',
    '4': '3-4 years',
    '5': '4-5 years',
    '6': '6-7 years',
    '7': '7-10 years',
    '8': '10-15 years',
    '9': 'more than 15 years',
  };

  @property()
  chronicCondition: boolean;

  @property({ comment: 'Family history of the insured', getType: () => FamilyHistory })
  familyHistory: FamilyHistory;

  @property({ comment: 'Personal history of the insured', getType: () => PersonalHistory })
  personalHistory: PersonalHistory;

  @property({ comment: 'Risk factores of the insured', getType: () => RiskFactors })
  riskFactors: RiskFactors;

  @property({ comment: 'Existing policies of the insured', getType: () => ExistingPolicies })
  existingPolicies: ExistingPolicies;

  @property({
    coment: 'Chronic Illness Rider supplements of the insured',
    getType: () => ChronicIllnessRiderSupplement,
  })
  chronicIllnessRiderSupplement: ChronicIllnessRiderSupplement;

  @property({ comment: 'Financial info of the insured', getType: () => Financials })
  financials: Financials;

  @property()
  selectedQuote;

  @property({
    rule: ['currency'],
    values: [
      '$100,000',
      '$200,000',
      '$300,000',
      '$400,000',
      '$500,000',
      '$600,000',
      '$700,000',
      '$800,000',
      '$900,000',
      '$1,000,000',
      '$1,200,000',
      '$1,400,000',
      '$1,500,000',
      '$1,700,000',
      '$1,900,000',
      '$2,000,000',
      '$2,500,000',
      '$3,000,000',
    ],
    comment: 'Insurance policy face amount the customer is applying',
  })
  overrideAmount: number = 5e5;

  @property({
    rule: ['numeric'],
    values: [5, 10, 15, 20, 20, 25, 30],
    descriptions: {
      '5': '5 years',
      '10': '10 years',
      '15': '15 years',
      '20': '20 years',
      '25': '25 years',
      '30': '30 years',
    },
    comment: 'Insurance policy term the customer is applying',
  })
  overrideTerm: number = 20;

  @property()
  policyHidden: boolean;

  @property()
  policyAppliedFor: boolean;

  @property({ getType: () => Customer })
  customer: Customer;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ type: Array, isLocal: true, getType: () => PolicyQuery })
  policiesGoneThruAU: Array<PolicyQuery>;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ type: Array, isLocal: true, getType: () => PolicyQuery })
  policiesGoneThruIU: Array<PolicyQuery>;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ type: Array, isLocal: true, getType: () => PolicyQuery })
  policiesGoneThruFU: Array<PolicyQuery>;

  @property({ isLocal: true })
  addressSameAsPrimary: boolean;

  @property()
  firstName;

  @property({ isLocal: true, persist: false })
  applicantType: string;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property()
  useAlcohol: boolean;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  @property({ sensitiveData: true, comment: 'Universally unique identifier' })
  uuid: string;

  @property({ toClient: false, persist: false, getType: () => BrothersAndSisters })
  _docusign_BrothersAndSisters: Array<BrothersAndSisters>;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  //if a person lived to a ripeOldAge, we ignore their diseases
  ripeOldAge: number = 80;

  /** Hit-Decoupling: Not referenced in any code (TODO: HL-39780) */
  ageAtOnsetThreshold: number = 70;

  @property({ toServer: false })
  reapplyTokensLeft: number = 0;

  @property({ toServer: false })
  reapplyTokensUsed: number = 0;

  constructor() {
    super();
  }

  static new(customer) {
    return new Applicant().init(customer);
  }

  init(customer) {
    this.customer = customer;
    this.person = Person.new(customer);
    this.address = Address.new(customer);
    this.verificationAddress = Address.new(customer);
    this.phone = Phone.new(customer, Phone.TYPES.mobile);
    this.personalHistory = PersonalHistory.new(customer);
    this.familyHistory = FamilyHistory.new(customer);
    this.riskFactors = RiskFactors.new(customer);
    this.existingPolicies = ExistingPolicies.new(customer);
    this.financials = Financials.new(customer);
    this.person.examAddress = Address.new(this.customer);
    this.doctorAddress = Address.new(this.customer);
    this.signer = Signer.new();

    return this;
  }

  clientInit() {}

  get person(): Person | null {
    return this.party instanceof Person ? this.party : null;
  }

  set person(person) {
    this.party = person;
  }

  get address() {
    return this.person ? this.person.residentialAddress : null;
  }

  set address(address) {
    this.person.residentialAddress = address;
  }

  get verificationAddress() {
    return this.person ? this.person.verificationAddress : null;
  }

  set verificationAddress(address) {
    this.person.verificationAddress = address;
  }

  get phone() {
    return this.party ? this.party.phone : null;
  }

  set phone(phone) {
    this.party.phone = phone;
  }

  getHeight(): string {
    return `${this.heightFeet}'${this.heightInches}"`;
  }

  heightInchesTrigger() {
    this.height = this.heightFeet * 12 + this.heightInches;
  }

  heightFeetTrigger() {
    this.height = this.heightFeet * 12 + this.heightInches;
  }

  healthClassNumberTrigger() {
    this.healthClass = ['Rg', 'R+', 'Pf', 'P+'][Math.round(this.healthClassNumber)] + (this.smoker ? 'S' : '');
  }

  bloodPressureRangeTrigger() {
    this.bloodPressureSystolic = this.bloodPressureSystolicRanges[this.bloodPressureRange];
    this.bloodPressureDystolic = this.bloodPressureDystolicRanges[this.bloodPressureRange];
  }

  bloodPressureDystolicTrigger() {
    this.bloodPressureSystolicTrigger();
  }

  bloodPressureSystolicTrigger() {
    let systolicRange;
    let dystolicRange;

    for (systolicRange = 0; systolicRange < 4; ++systolicRange) {
      if (this.bloodPressureSystolic <= this.bloodPressureSystolicRanges[String(systolicRange)]) {
        break;
      }
    }

    for (dystolicRange = 0; dystolicRange < 4; ++dystolicRange) {
      if (this.bloodPressureDystolic <= this.bloodPressureDystolicRanges[String(dystolicRange)]) {
        break;
      }
    }

    this.bloodPressureRange = String(Math.max(systolicRange, dystolicRange));
  }

  /*http://www.heart.org/HEARTORG/Conditions/HighCholestoral/AboutHighCholestoral/Understanding-Blood-Pressure-Readings_UCM_301764_Article.jsp*/
  cholesterolRangeTrigger() {
    this.cholesterolLevel = this.cholesterolLevelRanges[this.cholesterolRange];
    this.cholesterolRatio = this.cholesterolRatioRanges[this.cholesterolRange];
  }

  cholesterolLevelTrigger() {
    this.cholesterolRatioTrigger();
  }

  cholesterolRatioTrigger() {
    let cholesterolLevel;
    let cholesterolRatio;

    for (cholesterolLevel = 0; cholesterolLevel < 3; ++cholesterolLevel) {
      if (this.cholesterolLevel <= this.cholesterolLevelRanges[cholesterolLevel]) {
        break;
      }
    }

    for (cholesterolRatio = 0; cholesterolRatio < 3; ++cholesterolRatio) {
      if (this.cholesterolRatio <= this.cholesterolRatioRanges[cholesterolRatio]) {
        break;
      }
    }

    if (!this.cholesterolHigh) {
      return 'No';
    }

    this.cholesterolRange = Math.max(cholesterolLevel, cholesterolRatio);
  }

  smokerTrigger() {
    this.healthClass = ['Rg', 'R+', 'Pf', 'P+'][Math.round(this.healthClassNumber)] + (this.smoker ? 'S' : '');
  }

  overrideAmountTrigger() {
    this.overrideAmount = this.customer.capitalNeeds.getCoverage(false);
  }

  hasSignerTrigger() {
    const primaryPolicy = this.customer.application.primaryPolicy;

    // save old values
    const signerFirstName: string = this.signer.firstName;
    const signerMiddleName: string = this.signer.middleName;
    const signerLastName: string = this.signer.lastName;

    if (this.hasSigner) {
      /**
       * Will 'signer' own this policy? = No
       * override 'signer' object with new Signer object
       */
      this.signer = Signer.new(this.customer);
    } else {
      /**
       * 'trust' is no longer a valid option for 'ownerClassification'
       * reset ownerClassification if necessary
       */
      if (primaryPolicy.ownerClassification === 'trust') {
        primaryPolicy.ownerClassification = null;
        primaryPolicy.ownerClassificationTrigger();
      }

      // Will 'signer' own this policy? = Yes
      this.signer = primaryPolicy.ownerPerson;
      // If answer of "signer own this policy?" is changed from No to Yes,
      // then clear out the answer of "relationship to insure" in owner information section
      // of the application form
      primaryPolicy.ownerTypeSelection = null;
      primaryPolicy.ownerTypeSelectionTrigger();
      primaryPolicy.insured.person.email = this.signer.email;
    }

    // put back old values for 'signer' object
    this.signer.firstName = signerFirstName;
    this.signer.middleName = signerMiddleName;
    this.signer.lastName = signerLastName;
  }

  smokedInTheLastYear() {
    return this.riskFactors.isSmokerYear === 'last12';
  }

  getAge(asOfDate?) {
    return this.person.getAge(asOfDate);
  }

  getInsuranceAge(asOfDate?) {
    return this.person.getInsuranceAge(asOfDate);
  }

  getInsuranceAgeDate(asOfDate?) {
    return this.person.getInsuranceAgeDate(asOfDate);
  }

  isTrue() {
    return true;
  }

  isFalse() {
    return false;
  }

  isBlank() {
    return '';
  }

  get BrothersAndSisters(): Array<BrothersAndSisters> {
    this._docusign_BrothersAndSisters = [];

    if (this.familyHistory.brothersLiving !== 'Nobiologicalbrothers') {
      this._docusign_BrothersAndSisters.push(BrothersAndSisters.new('Brother(s)', this.familyHistory.brothersLiving));
    }

    if (this.familyHistory.sistersLiving !== 'Nobiologicalsisters') {
      this._docusign_BrothersAndSisters.push(BrothersAndSisters.new('Sister(s)', this.familyHistory.sistersLiving));
    }

    for (let i = this._docusign_BrothersAndSisters.length; i < 2; i++) {
      this._docusign_BrothersAndSisters.push(BrothersAndSisters.new('', ''));
    }

    return this._docusign_BrothersAndSisters;
  }

  getSpouse(): Applicant {
    return this === this.customer.primaryCustomer ? this.customer.alternateCustomer : this.customer.primaryCustomer;
  }

  isFemaleAndPregnant() {
    return !this.person.isMale() && this.riskFactors.isPregnant;
  }

  hadMasProsOopherectomy() {
    return this.person.isMale() ? this.personalHistory.hasProstatectomy : this.personalHistory.hadMastectomy;
  }

  get bodyMassIndex(): number {
    const weightInKilograms = this.weight / 2.205;
    const heightInMeters = this.height / 39.37;
    return weightInKilograms / Math.pow(heightInMeters, 2);
  }

  async addReapplyToken() {
    this.reapplyTokensLeft++;
    await this.persistSave();
  }

  removeReapplyToken() {
    if (this.reapplyTokensLeft > 0) {
      this.reapplyTokensLeft--;
      return this.persistSave();
    }
  }

  applyTokenUsed() {
    if (this.reapplyTokensLeft > 0) {
      this.reapplyTokensLeft--;
      this.reapplyTokensUsed++;
    }
  }

  async setReapplyNegative() {
    this.reapplyTokensLeft = -1;
    await this.persistSave();
  }

  getInsuredSigner() {
    return this.hasSigner ? this.signer : this.person;
  }
}
